import { nextTick } from 'vue';

import { getCueConfigByAction } from '../components/ListCues/cueListConfig';
import { CUE_PROPERTY_TYPES, SURVEY_LAUNCH_OPTIONS } from '../config/constants';

import analytics from '@/analytics';
import { getAMPMFromDate } from '@/assets/utilfunctions';
import logger from '@/services/logger.service';
import store from '@/store';
import { LiveRunOfShowCue } from '@/store/modules/event/types';

//check http or https and valid url
export function validateTickerUrl(url: string, allowEmpty: boolean = true): boolean {
  if (!url && allowEmpty) {
    return true;
  }
  const regex = /^(https?:\/\/)(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,63}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)$/;
  return regex.test(url);
}

export function validateTickerMessage(message: string): boolean {
  const trimmed = message.trim();
  return trimmed.length > 0 && trimmed.length <= 50;
}

export function validateTickerInput(message: string, url: string): boolean {
  return validateTickerMessage(message) && validateTickerUrl(url);
}

export const getRouteInfo = () => ({
  eventId: store.state.route.params?.eventID,
  broadcastId: store.state.route.params?.id
});

export const trackAnalytics = (event: string, additionalData: Record<string, any> = {}) => {
  const { eventId, broadcastId } = getRouteInfo();
  analytics.track(event, {
    eventId,
    broadcastId,
    ...additionalData
  });
};

export const getCurrentBroadcast = () => store.getters['event/currentBroadcast'];

export const getCueProperty = (cue: LiveRunOfShowCue, propertyType: string) =>
  cue?.cue_properties?.find(p => p.value_type === propertyType)?.value;

const hasValidScheduleFields = (openTime?: string, duration?: string, launchEnum?: string): boolean => {
  return Boolean(
    openTime &&
      duration &&
      launchEnum &&
      openTime !== '' &&
      duration !== '' &&
      launchEnum !== '' &&
      openTime !== '0' &&
      duration !== '0'
  );
};

export const isSurveyCueScheduled = (cue: LiveRunOfShowCue): boolean => {
  const toggleValue = getCueProperty(cue, CUE_PROPERTY_TYPES.SURVEY_TOGGLE);
  if (toggleValue !== undefined) {
    return toggleValue === 'true';
  }

  const openTime = getCueProperty(cue, CUE_PROPERTY_TYPES.SURVEY_OPEN_TIME);
  const duration = getCueProperty(cue, CUE_PROPERTY_TYPES.SURVEY_DURATION);
  const launchEnum = getCueProperty(cue, CUE_PROPERTY_TYPES.SURVEY_LAUNCH_ENUM);

  return hasValidScheduleFields(openTime, duration, launchEnum);
};

export const WAIT_STAGE_CLEAR_TIMEOUT_MS = 3000;
export const WAIT_STAGE_CLEAR_INTERVAL_MS = 50;

export async function waitUntilStageClearsOrOverridable(): Promise<void> {
  const startTime = Date.now();

  while (Date.now() - startTime < WAIT_STAGE_CLEAR_TIMEOUT_MS) {
    const publishedStage = store?.getters['sidebar/publishedStage'];

    if (!publishedStage?.currentScreen || publishedStage?.isOverridable) {
      await nextTick();
      return;
    }

    await new Promise(resolve => setTimeout(resolve, WAIT_STAGE_CLEAR_INTERVAL_MS));
  }
}

export async function clearStageItemBeforePublishing() {
  const publishedStage = store.getters['sidebar/publishedStage'];
  if (publishedStage?.currentScreen) {
    await store.dispatch('sidebar/unpublish');
    await waitUntilStageClearsOrOverridable();
  }
}

export const getSurveyIdentity = (cue: LiveRunOfShowCue): string => {
  const surveyId = getCueProperty(cue, CUE_PROPERTY_TYPES.SURVEY) ?? '';
  const openTime = getCueProperty(cue, CUE_PROPERTY_TYPES.SURVEY_OPEN_TIME) ?? '';
  const duration = getCueProperty(cue, CUE_PROPERTY_TYPES.SURVEY_DURATION) ?? '';
  const launchEnum = getCueProperty(cue, CUE_PROPERTY_TYPES.SURVEY_LAUNCH_ENUM) ?? '';
  const scheduleToggle = getCueProperty(cue, CUE_PROPERTY_TYPES.SURVEY_TOGGLE) ?? '';
  return [surveyId, openTime, duration, launchEnum, scheduleToggle, cue.id].join('|');
};

export const getNextAvailableCue = (cueList: LiveRunOfShowCue[]): LiveRunOfShowCue | null => {
  return (
    [...cueList]
      .sort((a, b) => (a.score || 0) - (b.score || 0))
      .find(cue => {
        const cueConfig = getCueConfigByAction(cue.action);
        return cue.status !== 'COMPLETED' && !cueConfig?.isActive?.(cue);
      }) || null
  );
};

let currentActiveCueIndex = -1;

export const scrollToActiveCues = (cueList: LiveRunOfShowCue[]): void => {
  const activeCues = [...cueList]
    .sort((a, b) => (a.score || 0) - (b.score || 0))
    .filter(cue => {
      const cueConfig = getCueConfigByAction(cue.action);
      return cueConfig?.isActive?.(cue);
    });

  if (activeCues.length === 0) {
    currentActiveCueIndex = -1;
    return;
  }

  currentActiveCueIndex = (currentActiveCueIndex + 1) % activeCues.length;
  const targetCue = activeCues[currentActiveCueIndex];

  if (targetCue) {
    const element = document.querySelector(`[data-cue-id="${targetCue.id}"]`);
    element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
};

export function filterCues(cues: LiveRunOfShowCue[], filterType: 'all' | 'active'): LiveRunOfShowCue[] {
  if (filterType === 'all') {
    return cues;
  }

  return cues.filter(cue => {
    const cueConfig = getCueConfigByAction(cue.action);
    return cueConfig?.isActive?.(cue) ?? false;
  });
}

export const calculateSurveyLaunchTime = (
  broadcastStartTime: number,
  broadcastEndTime: number,
  openMinutes: number,
  launchType: string
): number => {
  if (launchType === SURVEY_LAUNCH_OPTIONS.AFTER_SESSION_BEGINS) {
    return broadcastStartTime + openMinutes * 60 * 1000;
  }
  if (launchType === SURVEY_LAUNCH_OPTIONS.BEFORE_SESSION_ENDS) {
    return broadcastEndTime - openMinutes * 60 * 1000;
  }
  return broadcastStartTime;
};

export const formatSurveyScheduleMessage = (launchTime: number, duration: number): string => {
  const surveyLaunchTime = new Date(launchTime);
  const timeString = getAMPMFromDate(surveyLaunchTime);

  return `Survey is scheduled to launch at ${timeString} for ${duration} minutes`;
};

export const handleLiveRunOfShowAutomation = () => {
  const broadcast = store.getters['event/currentBroadcast'];
  if (!broadcast?.is_live) {
    return;
  }

  const cues = store.state.event.liveRunOfShow.cues || [];
  const now = (store.state.event.stage?.heartBeatDate || new Date()).getTime();
  const broadcastStart = new Date(broadcast.live_start_time).getTime() || new Date(broadcast.start_time).getTime();
  const broadcastEnd = new Date(broadcast.live_end_time).getTime() || new Date(broadcast.end_time).getTime();

  for (const cue of cues) {
    if (cue.action !== 'LAUNCH_SURVEY' || cue.status === 'COMPLETED') {
      continue;
    }

    const surveyId = getCueProperty(cue, CUE_PROPERTY_TYPES.SURVEY);
    const openTime = getCueProperty(cue, CUE_PROPERTY_TYPES.SURVEY_OPEN_TIME);
    const duration = getCueProperty(cue, CUE_PROPERTY_TYPES.SURVEY_DURATION);
    const launchEnum = getCueProperty(cue, CUE_PROPERTY_TYPES.SURVEY_LAUNCH_ENUM);
    const scheduled = isSurveyCueScheduled(cue);

    if (!surveyId || !scheduled || !openTime || !duration || !launchEnum) {
      continue;
    }

    const openMinutes = parseInt(openTime);
    const durationMinutes = parseInt(duration);

    if (openMinutes === 0 || durationMinutes === 0) {
      continue;
    }

    const surveyStart = calculateSurveyLaunchTime(broadcastStart, broadcastEnd, openMinutes, launchEnum);
    const surveyEnd = surveyStart + durationMinutes * 60 * 1000;

    const publishedSurveyId = store.state.event?.survey?.publishedSurveyId;

    if (now >= surveyStart && now < surveyEnd && publishedSurveyId !== surveyId) {
      logger.info('Auto-launching survey', { surveyId });
      const surveyIdentity = getSurveyIdentity(cue);
      store.commit('event/setActiveCueId', surveyIdentity);
      store.dispatch('event/survey/publishSurvey', surveyId);
      trackAnalytics('survey_auto_launched_from_live_ros', { surveyId });
    }

    if (now >= surveyEnd && publishedSurveyId === surveyId) {
      logger.info('Auto-closing survey', { surveyId });
      store.dispatch('event/survey/unPublishSurvey', surveyId);
      store.dispatch('event/updateLiveRunOfShowCue', { cueId: cue.id, updates: { status: 'COMPLETED' } });
      store.commit('event/setActiveCueId', null);
      trackAnalytics('survey_auto_closed_from_live_ros', { surveyId });
    }
  }
};
